<template>
  <div class="content-carousel">
    <sc-text
      tag="h2"
      class="content-carousel__title"
      v-if="fields && fields.heading"
      :field="fields.heading"
    />

    <sc-rich-text
      tag="div"
      class="content-carousel__lead"
      v-if="fields && fields.leadCopy"
      :field="fields.leadCopy"
    />

    <div class="content-carousel__content">
      <template v-if="isEditing">
        <!--
          When experience editor is active we want to render all carousel slides, to simplify editing.
          @splide:move="splideIndex"
        -->
        <sc-placeholder name="jss-content-carousel" :rendering="rendering" />
      </template>
      <template v-else>
        <Splide
          ref="splide"
          v-if="hasContentPlaceholder"
          :options="options"
          aria-label="Carousel"
          @splide:move="updateCurrentSlide"
        >
          <SplideSlide
            ref="splideslide"
            v-for="(item, index) in $options.computed.contentPlaceholder"
            :is="item"
            :key="`item${index}`"
          >
            <component :is="item" :key="`item${index}`" />
          </SplideSlide>
        </Splide>
        <div class="mobileOnly">{{ currentSlide + 1 }} / {{ carouselSize }}</div>
      </template>
    </div>
  </div>
</template>

<script>
import { Text, RichText, Placeholder } from '@sitecore-jss/sitecore-jss-vue';
import { ref, reactive, shallowRef } from 'vue';
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import '@splidejs/vue-splide/css';

export default {
  name: 'CIS-Content-Carousel',
  components: {
    ScText: Text,
    ScRichText: RichText,
    ScPlaceholder: Placeholder,
    Splide,
    SplideSlide
  },
  props: {
    rendering: {
      type: Object
    },
    fields: {
      type: Object,
      default: () => ({})
    }
  },
  withPlaceholder() {
    return {
      placeholders: [
        {
          placeholder: 'jss-content-carousel',
          computedPropName: 'contentPlaceholder'
        }
      ]
    };
  },
  computed: {
    isEditing() {
      // this.$jss is defined on the Vue instance by the SitecoreJssPlugin and provides
      // reactive access to the `sitecoreContext` provided in layout service data.
      return this.$jss.sitecoreContext().pageEditing;
    },
    hasContentPlaceholder() {
      return this.$options?.computed?.contentPlaceholder?.length > 0;
    }
  },
  setup(props) {
    const splide = ref(null);
    const currentSlide = ref(0);
    const carouselSize = shallowRef(
      props?.rendering?.placeholders?.['jss-content-carousel']?.length
    );
    const slidesToShowDestop = shallowRef(props?.fields?.slidesToShowDestop?.value);
    const slidesToShowTablet = shallowRef(props?.fields?.slidesToShowTablet?.value);
    const slidesToShowMobile = shallowRef(props?.fields?.slidesToShowMobile?.value);

    // Destop - Slides to Show
    if (slidesToShowDestop?.value) {
      if (slidesToShowDestop?.value > carouselSize?.value)
        slidesToShowDestop.value = carouselSize?.value;
    } else {
      if (carouselSize?.value < 5) slidesToShowDestop.value = carouselSize?.value;
      else slidesToShowDestop.value = 5;
    }

    // Tablet - Slides to Show
    if (slidesToShowTablet?.value) {
      if (slidesToShowTablet?.value > carouselSize?.value)
        slidesToShowTablet.value = carouselSize?.value;
    } else {
      if (carouselSize?.value < 4) slidesToShowTablet.value = carouselSize?.value;
      else slidesToShowTablet.value = 4;
    }

    // Mobile - Slides to Show
    if (slidesToShowMobile?.value) {
      if (slidesToShowMobile?.value > carouselSize?.value)
        slidesToShowMobile.value = carouselSize?.value;
    } else {
      if (carouselSize?.value < 3) slidesToShowMobile.value = carouselSize?.value;
      else slidesToShowMobile.value = 3;
    }

    const options = reactive({
      type: 'loop',
      updateOnMove: true,
      arrows: carouselSize?.value <= slidesToShowDestop?.value ? false : true,
      rewind: true,
      pagination: true,
      perPage: slidesToShowDestop?.value,
      perMove: slidesToShowDestop?.value,
      autoplay:
        carouselSize?.value <= slidesToShowDestop?.value ? false : props?.fields?.autoplay?.value,
      drag: carouselSize?.value <= slidesToShowDestop?.value ? false : true,
      speed: props?.fields?.carouselSpeed?.value || 2000,
      interval: 4000,
      padding: { left: 34, right: 34 },
      breakpoints: {
        1024: {
          perPage: slidesToShowTablet?.value,
          perMove: slidesToShowTablet?.value,
          arrows: carouselSize?.value <= slidesToShowTablet?.value ? false : true,
          autoplay:
            carouselSize?.value <= slidesToShowTablet?.value
              ? false
              : props?.fields?.autoplay?.value,
          drag: carouselSize?.value <= slidesToShowTablet?.value ? false : true
        },
        480: {
          perPage: slidesToShowMobile?.value,
          perMove: slidesToShowMobile?.value,
          arrows: carouselSize?.value <= slidesToShowMobile?.value ? false : true,
          autoplay:
            carouselSize?.value <= slidesToShowMobile?.value
              ? false
              : props?.fields?.autoplay?.value,
          drag: carouselSize?.value <= slidesToShowMobile?.value ? false : true
        }
      }
    });

    const updateCurrentSlide = (slide) => {
      currentSlide.value = slide.index;
    };

    return { splide, options, carouselSize, currentSlide, updateCurrentSlide };
  }
};
</script>
<style lang="scss">
.content-carousel {
  margin-top: 4rem;
  margin-bottom: 4rem;

  &__lead {
    margin-bottom: 3rem;
  }

  &__content {
    padding: 2rem 2rem 0 2rem;
  }

  .mobileOnly {
    text-align: center;
    margin-top: 1.5rem;
    @include font-styles('Arial Narrow', 1.6rem, 400, normal, 1.6rem, 0, $dark-blue);

    @media (min-width: 481px) {
      display: none;
    }
  }
}
</style>
